import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Building2, 
  Eye,
  Filter,
  CreditCard,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const DuePaymentsOverview = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overallDue, setOverallDue] = useState({
    driverDue: 0,
    vendorDue: 0,
    totalDue: 0
  });
  const [regionalOffices, setRegionalOffices] = useState([]);
  const [developerFilter, setDeveloperFilter] = useState('non-developer'); // 'all', 'developer', 'non-developer'

  const canView = hasPermission('financial', 'view') || hasPermission('payments', 'view');

  const fetchOverallDue = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const params = {};
      if (developerFilter !== 'all') {
        params.developerOnly = developerFilter === 'developer';
      }
      const response = await api.get('/payments/due/overall', { params });
      console.log('Overall due response:', response.data);
      setOverallDue(response.data || {
        driverDue: 0,
        vendorDue: 0,
        totalDue: 0
      });
    } catch (error) {
      console.error('Error fetching overall due:', error);
      setOverallDue({
        driverDue: 0,
        vendorDue: 0,
        totalDue: 0
      });
    } finally {
      setLoading(false);
    }
  }, [developerFilter, canView]);

  const fetchRegionalOffices = useCallback(async () => {
    if (!canView) return;
    try {
      const params = {};
      if (developerFilter !== 'all') {
        params.developerOnly = developerFilter === 'developer';
      }
      const response = await api.get('/payments/due/regional-offices', { params });
      console.log('Regional offices due response:', response.data);
      setRegionalOffices(response.data || []);
    } catch (error) {
      console.error('Error fetching regional offices:', error);
      setRegionalOffices([]);
    }
  }, [developerFilter, canView]);

  useEffect(() => {
    if (canView) {
      fetchOverallDue();
      fetchRegionalOffices();
    }
  }, [canView, fetchOverallDue, fetchRegionalOffices]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (!canView) {
    return (
      <div className="empty-state">
        <CreditCard className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to view due payments.
        </p>
      </div>
    );
  }

  if (loading && !overallDue.totalDue) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Due Payments</h1>
        <p className="text-gray-600">Manage and track pending payments to drivers and vendors</p>
      </div>

      {/* Overall Due Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Driver Due</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(overallDue.driverDue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Vendor Due</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(overallDue.vendorDue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <span className="text-2xl font-bold text-red-600">₹</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Due</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(overallDue.totalDue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Regional Offices Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
          <h2 className="text-lg font-medium text-gray-900">Due by Regional Office</h2>
          {/* Developer Filter in header */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Developer:</span>
            <div className="flex space-x-1">
              {[
                { value: 'non-developer', label: 'Exclude-Dev', color: 'blue' },
                { value: 'all', label: 'Include-Dev', color: 'blue' },
                { value: 'developer', label: 'Dev Only', color: 'blue' },
               
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setDeveloperFilter(option.value)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    developerFilter === option.value
                      ? `bg-${option.color}-100 text-${option.color}-800 border-${option.color}-200 border`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Regional Office
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {regionalOffices.length > 0 ? (
                regionalOffices.map((office) => (
                  <tr key={office._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{office.name}</div>
                      <div className="text-sm text-gray-500">{office.address}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(office.driverDue || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(office.vendorDue || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency((office.driverDue || 0) + (office.vendorDue || 0))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          const params = new URLSearchParams({ from: 'financial' });
                          if (developerFilter !== 'all') {
                            params.set('developerOnly', developerFilter === 'developer');
                          }
                          navigate(`/financial/due/${office._id}?${params.toString()}`);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 flex items-center"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    {loading ? 'Loading regional offices...' : 'No regional offices found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default DuePaymentsOverview;

